// FinanceDeficit — the Deficit / Carryover tracker (migration 0074).
//
// Why this page exists: Dan/RJ flagged that UNPAID FY26 vendor invoices don't
// disappear at year-end — they bleed into FY27 and land on individual committee
// budgets. The flagship case is Transdev Fleet Services: ~$209k billed FY26,
// only ~$28.5k paid, leaving ~$180.7k unpaid that carries into FY27 on Apparatus
// & Maintenance — against a PO of only ~$30k and far more than the Transdev FY27
// line. The Board needs to see which committees are blown out BEFORE FY27 starts.
//
// Three surfaces, all numbers from the API (nothing hardcoded — even the headline
// deficit is the server-computed total_unpaid_carryover):
//   1. Headline KPIs — total unpaid FY26 carrying to FY27, # vendors affected,
//      and the single biggest exposure.
//   2. By-committee impact table — FY27 budget vs unpaid carryover → projected
//      FY27 need (budget + carryover) + over/under, so the Board sees which
//      committees are over before FY27 begins. Carryover committees are flagged.
//   3. Vendor invoice tracker — per vendor, the monthly invoice ledger with
//      status badges + actions, billed/paid/unpaid subtotals, a PO-coverage
//      callout (billed vs PO total vs uncovered), and editable rows (mark paid,
//      edit amount, fill a TBD month) + an add-invoice / add-vendor form.
//
// Edit gating is server-side (edit_finance); rows render the controls and rely on
// the PATCH/POST 403 surfaced through useSaveStatus (matching FinanceProjects).

import { useMemo, useState } from "react";
import {
  useDeficit,
  useInvoices,
  createInvoice,
  updateInvoice,
  deleteInvoice,
} from "../hooks/useFinance";
import type {
  DeficitVendor,
  DeficitCommittee,
  InvoiceTrackingRow,
  InvoiceStatus,
} from "../hooks/useFinance";
import { useSaveStatus } from "../hooks/useSaveStatus";
import KPICard from "../components/dashboard/KPICard";
import ChartCard from "../components/dashboard/ChartCard";
import PageShell from "../components/shared/PageShell";
import PageHeader from "../components/shared/PageHeader";
import PageIntro from "../components/shared/PageIntro";
import { usd0 } from "../components/finance/charts";
import { currentFiscalYear, priorFiscalYear } from "../lib/fiscal";
import {
  TH,
  THR,
  TD,
  TDR,
  FetchErrorBanner,
  BTN_BRAND,
} from "../components/finance/ui";

const num = (v: number | null | undefined): number => Number(v) || 0;

// ── Status badge (Paid green / Verify amber / Pending gray) ───────────────────
const STATUS_CLR: Record<InvoiceStatus, string> = {
  paid: "bg-status-green/15 text-status-green",
  verify: "bg-status-amber/15 text-status-amber",
  pending: "bg-theme-overlay text-theme-muted",
};
function StatusBadge({ status }: { status: InvoiceStatus | string }) {
  const clr =
    STATUS_CLR[status as InvoiceStatus] ?? "bg-theme-overlay text-theme-muted";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold capitalize ${clr}`}
    >
      {status === "paid" ? "✓ " : ""}
      {status}
    </span>
  );
}

const amt = (v: number | null): string => (v == null ? "TBD" : usd0(v));

export default function FinanceDeficit() {
  const { data, isLoading, refresh, error } = useDeficit();

  // This tracker always compares the CLOSING fiscal year (whose unpaid invoices
  // bleed forward) against the year we're now IN — never hardcode FY26/FY27
  // literals here, or the labels go stale at the next rollover. See the
  // fiscal_year comments on AddInvoiceRow/AddVendorOrInvoice below, which
  // already derive this the same way for the data they submit.
  const curFy = priorFiscalYear(currentFiscalYear());
  const nextFy = currentFiscalYear();

  const byVendor = data?.by_vendor ?? [];
  const byCommittee = data?.by_committee ?? [];
  const totalCarryover = num(data?.total_unpaid_carryover);
  const vendorCount = num(data?.vendor_count);
  const totalTbd = num(data?.total_tbd_count);

  // Biggest single exposure (vendors are returned sorted by unpaid desc).
  const topVendor = byVendor[0];

  return (
    <PageShell>
      <div>
        <PageHeader
          eyebrow="FINANCE"
          title="Deficit & Carryover"
          subtitle={`Unpaid ${curFy} vendor invoices don't vanish at year-end — they carry into ${nextFy} and land on individual committee budgets. This tracks what's unpaid, which committees it blows out, and how far each vendor's billing has run past its purchase order.`}
        />
        <PageIntro>
          The {curFy} unpaid total carrying into {nextFy} — by vendor and by
          committee — so the Board can plan the deficit before next year even
          starts.
        </PageIntro>
      </div>

      <FetchErrorBanner error={error} what="the deficit/carryover data" />

      {/* ── Headline KPIs ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KPICard
          label={`Unpaid ${curFy} → ${nextFy} (Deficit)`}
          value={usd0(totalCarryover)}
          subtitle={
            totalTbd > 0
              ? `${totalTbd} invoice${totalTbd === 1 ? "" : "s"} still TBD (not counted)`
              : `Carries into ${nextFy} as committee expense`
          }
          accent="red"
          isLoading={isLoading}
        />
        <KPICard
          label="Vendors Affected"
          value={vendorCount}
          subtitle={`With unpaid ${curFy} invoices carrying forward`}
          accent="amber"
          isLoading={isLoading}
        />
        <KPICard
          label="Biggest Single Exposure"
          value={topVendor ? usd0(topVendor.unpaid_total) : "—"}
          subtitle={topVendor ? topVendor.vendor : "No carryover"}
          accent="red"
          isLoading={isLoading}
        />
      </div>

      {/* ── By-committee impact (the planning view) ── */}
      <ChartCard
        title={`Committee Impact — ${nextFy} Budget vs ${curFy} Carryover`}
        subtitle={`Projected ${nextFy} need = the committee's recurring ${nextFy} budget plus the unpaid ${curFy} carryover landing on it. Committees carrying a deficit are flagged.`}
      >
        <CommitteeImpactTable
          rows={byCommittee}
          isLoading={isLoading}
          curFy={curFy}
          nextFy={nextFy}
        />
      </ChartCard>

      {/* ── Per-vendor invoice tracker ── */}
      {byVendor.map((v) => (
        <VendorTracker
          key={v.vendor}
          vendor={v}
          onChanged={refresh}
          nextFy={nextFy}
        />
      ))}

      <AddVendorOrInvoice
        onAdded={refresh}
        knownCommittees={byCommittee.map((c) => c.committee)}
      />

      {/* ── Year-end mechanics note ── */}
      <div className="rounded-lg border border-theme-subtle bg-theme-overlay/40 p-4 text-sm text-theme-secondary leading-relaxed">
        <p className="font-semibold text-theme-primary mb-1">
          How this feeds the {nextFy} Bridge
        </p>
        <p>
          The total unpaid carryover above feeds the {nextFy} Operating Bridge{" "}
          <span className="font-medium">&ldquo;carryover&rdquo;</span>{" "}
          adjustment line (the WEVS/FVS held-invoice line on the {nextFy} Budget
          &rarr; Bridge tab). Year-end mechanics: invoices billed before the{" "}
          <span className="font-medium">June 23</span> cutoff that are{" "}
          <span className="italic">not</span> on a PO carried via the{" "}
          <span className="font-medium">June 28 PO Carryforward</span> become
          new {nextFy} expense rather than a prior-year obligation. The{" "}
          <span className="font-medium">Uncovered</span> column on each vendor
          is exactly that exposure — unpaid billing with no purchase order
          behind it.
        </p>
      </div>
    </PageShell>
  );
}

// ── By-committee impact table ─────────────────────────────────────────────────
function CommitteeImpactTable({
  rows,
  isLoading,
  curFy,
  nextFy,
}: {
  rows: DeficitCommittee[];
  isLoading: boolean;
  curFy: string;
  nextFy: string;
}) {
  if (isLoading && rows.length === 0)
    return (
      <div className="py-8 text-center text-theme-muted text-sm">Loading…</div>
    );
  if (rows.length === 0)
    return (
      <div className="py-8 text-center text-theme-muted text-sm">
        No committees carrying an unpaid {curFy} deficit.
      </div>
    );

  const totals = rows.reduce(
    (acc, r) => {
      acc.budget += num(r.fy27_budget);
      acc.carryover += num(r.unpaid_carryover);
      acc.need += num(r.projected_need);
      return acc;
    },
    { budget: 0, carryover: 0, need: 0 },
  );

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-theme-subtle">
            <th className={TH}>Committee</th>
            <th className={THR}>{nextFy} Budget</th>
            <th className={THR}>Unpaid {curFy} Carryover</th>
            <th className={THR}>Projected {nextFy} Need</th>
            <th className={THR}>Over / Under</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const carrying = num(r.unpaid_carryover) > 0;
            // Over/under the budget once the carryover is layered on. A positive
            // carryover always pushes the projected need OVER the budget.
            const overBy = num(r.projected_need) - num(r.fy27_budget);
            return (
              <tr
                key={r.committee}
                className="border-b border-theme-subtle/60 hover:bg-theme-overlay/40 transition-colors"
              >
                <td
                  className={`${TD} ${carrying ? "font-semibold text-theme-primary" : ""}`}
                >
                  {carrying && (
                    <span
                      className="inline-block h-2 w-2 rounded-full bg-status-red mr-2 align-middle"
                      aria-hidden="true"
                    />
                  )}
                  {r.committee}
                </td>
                <td className={TDR}>{usd0(num(r.fy27_budget))}</td>
                <td
                  className={`${TDR} ${carrying ? "text-status-red font-semibold" : ""}`}
                >
                  {carrying ? `+${usd0(num(r.unpaid_carryover))}` : "—"}
                </td>
                <td className={`${TDR} font-semibold`}>
                  {usd0(num(r.projected_need))}
                </td>
                <td
                  className={`${TDR} ${overBy > 0 ? "text-status-red" : "text-status-green"}`}
                >
                  {overBy > 0 ? `+${usd0(overBy)} over` : usd0(overBy)}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-theme-subtle font-semibold">
            <td className={TD}>Total</td>
            <td className={TDR}>{usd0(totals.budget)}</td>
            <td className={`${TDR} text-status-red`}>
              +{usd0(totals.carryover)}
            </td>
            <td className={TDR}>{usd0(totals.need)}</td>
            <td className={`${TDR} text-status-red`}>
              +{usd0(totals.need - totals.budget)} over
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── Per-vendor invoice tracker ────────────────────────────────────────────────
function VendorTracker({
  vendor,
  onChanged,
  nextFy,
}: {
  vendor: DeficitVendor;
  onChanged: () => void;
  nextFy: string;
}) {
  const { data, refresh } = useInvoices(vendor.vendor);
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const [adding, setAdding] = useState(false);

  function afterChange() {
    refresh();
    onChanged();
  }

  // PO-coverage verdict: billed far exceeds the PO behind it.
  const billed = num(vendor.billed_total);
  const poCov = num(vendor.po_coverage);
  const uncovered = num(vendor.uncovered);
  const overPo = poCov > 0 && billed > poCov;

  return (
    <ChartCard
      title={vendor.vendor}
      subtitle={`${vendor.committee ?? "Unassigned"} · billed ${usd0(billed)} · paid ${usd0(num(vendor.paid_total))} · unpaid ${usd0(num(vendor.unpaid_total))}`}
    >
      {/* PO-coverage callout */}
      <div
        className={`mb-4 rounded-lg border p-3 text-sm flex flex-wrap gap-x-6 gap-y-1 items-center ${
          overPo
            ? "border-status-red/40 bg-status-red/5"
            : "border-theme-subtle bg-theme-overlay/40"
        }`}
      >
        <span>
          <span className="text-theme-muted">Billed:</span>{" "}
          <span className="font-semibold tabular-nums">{usd0(billed)}</span>
        </span>
        <span>
          <span className="text-theme-muted">PO coverage:</span>{" "}
          <span className="font-semibold tabular-nums">
            {poCov > 0 ? usd0(poCov) : "—"}
          </span>
          {vendor.po_number && (
            <span className="text-theme-muted text-xs">
              {" "}
              ({vendor.po_number})
            </span>
          )}
        </span>
        <span>
          <span className="text-theme-muted">Uncovered:</span>{" "}
          <span
            className={`font-semibold tabular-nums ${uncovered > 0 ? "text-status-red" : "text-status-green"}`}
          >
            {usd0(uncovered)}
          </span>
        </span>
        {overPo && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-status-red/15 text-status-red">
            ⚠ Billed far exceeds PO
          </span>
        )}
        {vendor.tbd_count > 0 && (
          <span className="text-xs text-theme-muted">
            {vendor.tbd_count} month{vendor.tbd_count === 1 ? "" : "s"} TBD (not
            in totals)
          </span>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-theme-subtle">
              <th className={TH}>Period</th>
              <th className={THR}>Amount</th>
              <th className={TH}>Status</th>
              <th className={TH}>Action</th>
              <th className={TH}>Note</th>
              <th className={THR}>Edit</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <InvoiceRowEl key={r.id} row={r} onChanged={afterChange} />
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-theme-subtle font-semibold">
              <td className={TD}>Subtotals</td>
              <td className={TDR}>{usd0(billed)} billed</td>
              <td className={`${TD} text-status-green`} colSpan={2}>
                {usd0(num(vendor.paid_total))} paid
              </td>
              <td className={`${TD} text-status-red`} colSpan={2}>
                {usd0(num(vendor.unpaid_total))} unpaid → {nextFy}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="mt-3">
        {adding ? (
          <AddInvoiceRow
            vendor={vendor.vendor}
            committee={vendor.committee}
            poNumber={vendor.po_number}
            onClose={() => setAdding(false)}
            onAdded={() => {
              setAdding(false);
              afterChange();
            }}
          />
        ) : (
          <button
            onClick={() => setAdding(true)}
            className="text-sm text-dcvfd-accent hover:underline"
          >
            + Add an invoice / month for this vendor
          </button>
        )}
      </div>
    </ChartCard>
  );
}

// ── Editable invoice row ──────────────────────────────────────────────────────
function InvoiceRowEl({
  row,
  onChanged,
}: {
  row: InvoiceTrackingRow;
  onChanged: () => void;
}) {
  const save = useSaveStatus();
  const [editing, setEditing] = useState(false);
  const [amountStr, setAmountStr] = useState(
    row.amount == null ? "" : String(row.amount),
  );
  const [status, setStatus] = useState<InvoiceStatus>(row.status);
  const [note, setNote] = useState(row.note ?? "");

  async function markPaid() {
    const okSave = await save.run(
      () => updateInvoice(row.id, { status: "paid", bleeds_to_next_fy: 0 }),
      "mark paid",
    );
    if (okSave) onChanged();
  }

  async function saveEdit() {
    const parsed = amountStr.trim() === "" ? null : Number(amountStr);
    if (parsed != null && (!Number.isFinite(parsed) || parsed < 0)) {
      // surface inline via the save-status banner without hitting the server
      await save.run(
        () => Promise.reject(new Error("Amount must be ≥ 0 or blank (TBD)")),
        "edit",
      );
      return;
    }
    // If marking paid, stop the bleed; otherwise leave bleeds as-is by not sending it.
    const patch: Parameters<typeof updateInvoice>[1] = {
      amount: parsed,
      status,
      note: note.trim() || null,
    };
    if (status === "paid") patch.bleeds_to_next_fy = 0;
    const okSave = await save.run(
      () => updateInvoice(row.id, patch),
      "edit invoice",
    );
    if (okSave) {
      setEditing(false);
      onChanged();
    }
  }

  async function remove() {
    const okSave = await save.run(
      () => deleteInvoice(row.id),
      "delete invoice",
    );
    if (okSave) onChanged();
  }

  const tbd = row.amount == null;

  if (editing) {
    return (
      <tr className="border-b border-theme-subtle/60 bg-theme-overlay/30">
        <td className={TD}>{row.period_month ?? "—"}</td>
        <td className={TDR}>
          <input
            type="number"
            min={0}
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            placeholder="TBD"
            className="w-24 px-2 py-1 text-right rounded border border-theme-subtle bg-theme-surface tabular-nums"
          />
        </td>
        <td className={TD}>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as InvoiceStatus)}
            className="px-2 py-1 rounded border border-theme-subtle bg-theme-surface text-sm"
          >
            <option value="pending">pending</option>
            <option value="verify">verify</option>
            <option value="paid">paid</option>
          </select>
        </td>
        <td className={TD}>{row.action ?? "—"}</td>
        <td className={TD}>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="note"
            className="w-40 px-2 py-1 rounded border border-theme-subtle bg-theme-surface text-sm"
          />
        </td>
        <td className={`${TDR} whitespace-nowrap`}>
          <button
            onClick={saveEdit}
            disabled={save.saving}
            className={`px-2 py-1 rounded-md text-xs font-medium ${BTN_BRAND} disabled:opacity-50`}
          >
            Save
          </button>
          <button
            onClick={() => setEditing(false)}
            className="ml-1 px-2 py-1 rounded-md text-xs text-theme-muted hover:text-theme-primary"
          >
            Cancel
          </button>
          {save.error && (
            <div className="text-[11px] text-status-red mt-1 text-left">
              {save.error}
            </div>
          )}
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-theme-subtle/60 hover:bg-theme-overlay/40 transition-colors">
      <td className={TD}>{row.period_month ?? "—"}</td>
      <td className={`${TDR} ${tbd ? "text-theme-muted italic" : ""}`}>
        {amt(row.amount)}
      </td>
      <td className={TD}>
        <StatusBadge status={row.status} />
      </td>
      <td className={TD}>
        {row.action ? (
          <span
            className={`text-xs font-medium ${row.action === "No Money" ? "text-status-red" : "text-theme-secondary"}`}
          >
            {row.action}
          </span>
        ) : (
          <span className="text-theme-muted">—</span>
        )}
      </td>
      <td
        className={`${TD} max-w-[16rem] truncate`}
        title={row.note ?? undefined}
      >
        {row.note ?? "—"}
      </td>
      <td className={`${TDR} whitespace-nowrap`}>
        {row.status !== "paid" && (
          <button
            onClick={markPaid}
            disabled={save.saving}
            className="px-2 py-1 rounded-md text-xs font-medium border border-status-green/40 text-status-green hover:bg-status-green/10 transition-colors disabled:opacity-50"
          >
            Mark paid
          </button>
        )}
        <button
          onClick={() => setEditing(true)}
          className="ml-1 px-2 py-1 rounded-md text-xs text-dcvfd-accent hover:underline"
        >
          Edit
        </button>
        <button
          onClick={remove}
          className="ml-1 px-2 py-1 rounded-md text-xs text-status-red hover:bg-status-red/10 transition-colors"
        >
          ✕
        </button>
        {save.error && (
          <div className="text-[11px] text-status-red mt-1 text-left">
            {save.error}
          </div>
        )}
      </td>
    </tr>
  );
}

// ── Add-invoice row (within a vendor's table) ─────────────────────────────────
function AddInvoiceRow({
  vendor,
  committee,
  poNumber,
  onClose,
  onAdded,
}: {
  vendor: string;
  committee: string | null;
  poNumber: string | null;
  onClose: () => void;
  onAdded: () => void;
}) {
  const save = useSaveStatus();
  const [period, setPeriod] = useState("");
  const [amount, setAmount] = useState("");
  const [status, setStatus] = useState<InvoiceStatus>("pending");

  async function add() {
    const parsed = amount.trim() === "" ? null : Number(amount);
    if (parsed != null && (!Number.isFinite(parsed) || parsed < 0)) {
      await save.run(
        () => Promise.reject(new Error("Amount must be ≥ 0 or blank")),
        "add",
      );
      return;
    }
    const okSave = await save.run(
      () =>
        createInvoice({
          vendor,
          committee,
          // This tracker records the CLOSING year's unpaid invoices that bleed
          // into the next FY — so an invoice added here belongs to the year just
          // ended (prior to the clock's current FY), NOT the year we're now in.
          // Post-7/1 the clock is FY27 but these are FY26 invoices.
          fiscal_year: priorFiscalYear(currentFiscalYear()),
          period_month: period.trim() || null,
          amount: parsed,
          status,
          po_number: poNumber,
          // Anything not paid is treated as carrying into the next FY.
          bleeds_to_next_fy: status === "paid" ? 0 : 1,
        }),
      "add invoice",
    );
    if (okSave) onAdded();
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-theme-subtle bg-theme-overlay/40 p-3">
      <input
        value={period}
        onChange={(e) => setPeriod(e.target.value)}
        placeholder="Period (e.g. May-26)"
        className="w-36 px-2 py-1 rounded border border-theme-subtle bg-theme-surface text-sm"
      />
      <input
        type="number"
        min={0}
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        placeholder="Amount (blank = TBD)"
        className="w-40 px-2 py-1 rounded border border-theme-subtle bg-theme-surface text-sm tabular-nums"
      />
      <select
        value={status}
        onChange={(e) => setStatus(e.target.value as InvoiceStatus)}
        className="px-2 py-1 rounded border border-theme-subtle bg-theme-surface text-sm"
      >
        <option value="pending">pending</option>
        <option value="verify">verify</option>
        <option value="paid">paid</option>
      </select>
      <button
        onClick={add}
        disabled={save.saving}
        className={`px-3 py-1 rounded-md text-sm font-medium ${BTN_BRAND} disabled:opacity-50`}
      >
        Add
      </button>
      <button
        onClick={onClose}
        className="px-3 py-1 rounded-md text-sm text-theme-muted hover:text-theme-primary"
      >
        Cancel
      </button>
      {save.error && (
        <div className="w-full text-xs text-status-red">{save.error}</div>
      )}
    </div>
  );
}

// ── Add a whole new vendor (first invoice) ────────────────────────────────────
function AddVendorOrInvoice({
  onAdded,
  knownCommittees,
}: {
  onAdded: () => void;
  knownCommittees: string[];
}) {
  const save = useSaveStatus();
  const [open, setOpen] = useState(false);
  const [vendor, setVendor] = useState("");
  const [committee, setCommittee] = useState("");
  const [period, setPeriod] = useState("");
  const [amount, setAmount] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [status, setStatus] = useState<InvoiceStatus>("pending");

  const committees = Array.from(new Set(knownCommittees)).filter(Boolean);

  async function add() {
    if (!vendor.trim()) {
      await save.run(
        () => Promise.reject(new Error("Vendor is required")),
        "add vendor",
      );
      return;
    }
    const parsed = amount.trim() === "" ? null : Number(amount);
    if (parsed != null && (!Number.isFinite(parsed) || parsed < 0)) {
      await save.run(
        () => Promise.reject(new Error("Amount must be ≥ 0 or blank")),
        "add vendor",
      );
      return;
    }
    const okSave = await save.run(
      () =>
        createInvoice({
          vendor: vendor.trim(),
          committee: committee.trim() || null,
          // Closing year's carryover invoice — the year just ended, not the
          // current clock FY (post-7/1 the clock is FY27 but these are FY26).
          fiscal_year: priorFiscalYear(currentFiscalYear()),
          period_month: period.trim() || null,
          amount: parsed,
          status,
          po_number: poNumber.trim() || null,
          bleeds_to_next_fy: status === "paid" ? 0 : 1,
        }),
      "add vendor",
    );
    if (okSave) {
      setVendor("");
      setCommittee("");
      setPeriod("");
      setAmount("");
      setPoNumber("");
      setStatus("pending");
      setOpen(false);
      onAdded();
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={`px-4 py-2 rounded-md text-sm font-medium ${BTN_BRAND}`}
      >
        + Add a vendor
      </button>
    );
  }

  return (
    <ChartCard title="Add a vendor / invoice">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <input
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          placeholder="Vendor name *"
          className="px-3 py-2 rounded border border-theme-subtle bg-theme-surface text-sm"
        />
        <input
          list="deficit-committees"
          value={committee}
          onChange={(e) => setCommittee(e.target.value)}
          placeholder="Committee"
          className="px-3 py-2 rounded border border-theme-subtle bg-theme-surface text-sm"
        />
        <datalist id="deficit-committees">
          {committees.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
        <input
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          placeholder="Period (e.g. Jul-25)"
          className="px-3 py-2 rounded border border-theme-subtle bg-theme-surface text-sm"
        />
        <input
          type="number"
          min={0}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Amount (blank = TBD)"
          className="px-3 py-2 rounded border border-theme-subtle bg-theme-surface text-sm tabular-nums"
        />
        <input
          value={poNumber}
          onChange={(e) => setPoNumber(e.target.value)}
          placeholder="PO number"
          className="px-3 py-2 rounded border border-theme-subtle bg-theme-surface text-sm"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as InvoiceStatus)}
          className="px-3 py-2 rounded border border-theme-subtle bg-theme-surface text-sm"
        >
          <option value="pending">pending</option>
          <option value="verify">verify</option>
          <option value="paid">paid</option>
        </select>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button
          onClick={add}
          disabled={save.saving}
          className={`px-4 py-2 rounded-md text-sm font-medium ${BTN_BRAND} disabled:opacity-50`}
        >
          Add invoice
        </button>
        <button
          onClick={() => setOpen(false)}
          className="px-4 py-2 rounded-md text-sm text-theme-muted hover:text-theme-primary"
        >
          Cancel
        </button>
        {save.error && (
          <span className="text-xs text-status-red">{save.error}</span>
        )}
      </div>
    </ChartCard>
  );
}
